import React, { useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx-js-style";
import eventService from "../../services/eventService";
import divisionsService from "../../services/divisionService";
import teamService from "../../services/teamService";
import { useUser } from "../../hooks/useUser";
import brandsService from "../../services/brandSerivce";
import eventTypeService from "../../services/eventTypeService";
import spendTypeService from "../../services/spendTypeService";
import activityTypeService from "../../services/activityTypeService"

function EventsReport({ filters, selectedEvents }) {
  const [showSpinning, setShowSpinning] = useState(false);
  const { data: usersData } = useUser();

  const filterEvents = (events) => {
    if (!events) return [];

    return events.filter((event) => {
      // Only include selected events
      if (selectedEvents.length > 0 && !selectedEvents.includes(event.id)) {
        return false;
      }

      // Date Range Filter
      if (
        filters.dateRange.from &&
        new Date(event.start_date) < new Date(filters.dateRange.from)
      )
        return false;
      if (
        filters.dateRange.to &&
        new Date(event.start_date) > new Date(filters.dateRange.to)
      )
        return false;

      // Event Type Filter
      if (
        filters.selectedEventType &&
        event.event_type_id !== filters.selectedEventType
      )
        return false;

      // Division Filter
      if (
        filters.selectedDivision &&
        event.division_id !== filters.selectedDivision
      )
        return false;

      // Team Filter
      if (filters.selectedTeam && event.team_id !== filters.selectedTeam)
        return false;

      // Spend Type Filter
      if (
        filters.selectedSpendType &&
        event.spend_type_id !== filters.selectedSpendType
      )
        return false;

      // Activity Type Filter
      if (
        filters.selectedActivityType &&
        event.activity_type_id !== filters.selectedActivityType
      )
        return false;

      // Status Filter
      if (
        filters.selectedStatus &&
        event.status?.toLowerCase() !== filters.selectedStatus.toLowerCase()
      )
        return false;

      // Search Query Filter
      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        const matchesSearch =
          event.id?.toString().toLowerCase().includes(searchLower) ||
          event.promo_number?.toLowerCase().includes(searchLower) ||
          event.activity_type_id
            ?.toString()
            .toLowerCase()
            .includes(searchLower) ||
          event.division_id?.toString().toLowerCase().includes(searchLower) ||
          event.external_source_id
            ?.toString()
            .toLowerCase()
            .includes(searchLower) ||
          event.relationship_id
            ?.toString()
            .toLowerCase()
            .includes(searchLower) ||
          event.spend_type_id?.toString().toLowerCase().includes(searchLower) ||
          event.currency?.toLowerCase().includes(searchLower) ||
          event.name?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      return true;
    });
  };

  const eventTypeNames = {};
  const spendTypeNames = {};
  const activityTypeNames = {};
  const divisionNames = {};
  const teamNames = {};
  const brandNames = {};

  const getCached = async (cache, service, id) => {
    if (!id) return "";
    if (!cache[id]) cache[id] = await service.getNameById(id);
    return cache[id];
  };

  const handleExport = async () => {
    try {
      setShowSpinning(true);
      const workbook = XLSX.utils.book_new();
      const headers = [
        "Event ID",
        "Event Name",
        "Event Creator",
        "Event Type",
        "Spend Type",
        "Activity Type",
        "Division",
        "Team",
        "Brand",
        "Promo Number",
        "Start Date",
        "Status",
        "Attendee Count",
        "Event Total Cost",
        "Currency",
      ];

      // const eventData = await eventService.getAll();
      // const filteredEvents = filterEvents(eventData);

      const eventsWithAttendees =
        await eventService.getEventsDataWithEveryEventTheirAttendees();
      const filteredEvents = filterEvents(eventsWithAttendees);

      // Collect all rows here
      const rows = [headers];

      for (const event of filteredEvents) {
        const creatorName =
          usersData?.find((user) => user.id === event.created_by) || "";
        const eventTypeName = await getCached(
          eventTypeNames,
          eventTypeService,
          event.event_type_id,
        );
        const spendTypeName = await getCached(
          spendTypeNames,
          spendTypeService,
          event.spend_type_id,
        );
         const activityTypeName = await getCached(
          activityTypeNames,
          activityTypeService,
          event.activity_type_id,
        );

        const divisionName = await getCached(
          divisionNames,
          divisionsService,
          event.division_id,
        );
        const teamName = await getCached(teamNames, teamService, event.team_id);
        const eventTotalCost = event.spend_value || 0;
        const brandName = await getCached(
          brandNames,
          brandsService,
          event.brand_id,
        );
        const attendeeCount = event.attendees?.length || 0;
        rows.push([
          event.id,
          event.name,
          creatorName?.first_name + " " + creatorName?.family_name || "",
          eventTypeName,
          spendTypeName,
          activityTypeName,
          divisionName,
          teamName,
          brandName,
          event.promo_number,
          event.start_date,
          event.status,
          attendeeCount,
          eventTotalCost,
          event.currency,
        ]);
      }

      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      worksheet["!cols"] = headers.map(() => ({ width: 46 }));
      worksheet["!rows"] = [{ hpx: 28 }];

      // Apply styles (headers and cells)
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = worksheet[cell_address];
          if (cell) {
            if (R === 0) {
              cell.s = {
                fill: { fgColor: { rgb: "CCFFCC" } },
                font: { bold: true, sz: 12 },
                alignment: { horizontal: "center", vertical: "center" },
              };
            } else {
              cell.s = {
                alignment: { horizontal: "center", vertical: "center" },
              };
            }
          }
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, "Events Report");
      XLSX.writeFile(workbook, "Events_Report.xlsx");
    } catch (error) {
      toast.error("Failed to generate TOV Report. Please try again.");
    } finally {
      setShowSpinning(false);
    }
  };

  return (
    <button
      data-track="events-report-button"
      data-module="Events"
      onClick={handleExport}
      disabled={showSpinning}
      className={`bg-green-600 transition-colors duration-200 px-4 py-2 uppercase 
      rounded-lg text-white font-medium flex gap-2 items-center text-sm ${
        showSpinning
          ? "cursor-not-allowed bg-green-600/60"
          : "hover:bg-green-700"
      }`}
    >
      <i className="fa-solid fa-file-arrow-down"></i>
      {showSpinning ? "Exporting..." : "Events Report"}
      {showSpinning && (
        <svg
          className="animate-spin h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
    </button>
  );
}

export default EventsReport;
